import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import { TASKS, Q3_IDS, Q4_IDS } from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "time-raid";
const taskById = Object.fromEntries(TASKS.map((t) => [t.id, t]));

function computeScore({ reconResult, formation, raidResult }) {
  const reconScore = reconResult.correctCount * 15;
  const frogBonus = 100;
  const bufferBonus = 50;
  const shieldsBonus = formation.shieldsCount * 20;

  const q3q4Ids = [...Q3_IDS, ...Q4_IDS];
  const q3q4GoodCount = q3q4Ids.filter((id) => {
    const resId = formation.q3Resolutions[id] || formation.q4Resolutions[id];
    const opt = taskById[id].resolutionOptions.find((o) => o.id === resId);
    return opt?.tag === "good";
  }).length;
  const q3q4Bonus = q3q4GoodCount * 25;

  const raidGood = raidResult.log.filter((l) => l.choice.tag === "good").length;
  const raidBad = raidResult.log.filter((l) => l.choice.tag === "bad").length;
  const raidScore = raidGood * 20 - raidBad * 20;

  const raw = reconScore + frogBonus + bufferBonus + shieldsBonus + q3q4Bonus + raidScore + raidResult.focus;
  return Math.max(50, raw);
}

export default function DebriefStage({ reconResult, formation, raidResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const score = computeScore({ reconResult, formation, raidResult });
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + score) / 500) + 1 > (profile?.level ?? 1);
  const raidGood = raidResult.log.filter((l) => l.choice.tag === "good").length;
  const raidBad = raidResult.log.filter((l) => l.choice.tag === "bad").length;

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Time Raid results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">RAID DEBRIEF</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">Day Complete</p>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Recon Accuracy</span>
          <span className="text-cyan">{reconResult.correctCount}/8</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Frog Eaten First</span>
          <span className="text-lime">✔ Block 1 protected</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Buffer Reserved</span>
          <span className="text-lime">✔ One block held open</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Focus Shields Used</span>
          <span className="text-cyan">{raidResult.shieldsUsed}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Good Calls / Rough Calls</span>
          <span>
            <span className="text-lime">{raidGood}</span>
            <span className="text-mist/40"> / </span>
            <span className="text-danger">{raidBad}</span>
          </span>
        </div>
        <div className="flex justify-between border-t border-black/50 pt-2">
          <span className="text-mist/60">Focus Remaining</span>
          <span className="text-gold">{raidResult.focus}/100</span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{score} XP</span>
          )}
        </div>
      </div>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ RUN THE DAY AGAIN
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
